import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { ArrowRight, MapPin, Vote, Lock, Check, X } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import * as Haptics from 'expo-haptics';
import theme from '@/constants/colors';
import type { GroupChatMessage } from '@/lib/api/groupChat';
import { useVoteGroupPoll, useCloseGroupPoll } from '@/lib/queries/groupChat';
import { useAuth } from '@/context/AuthContext';
import { useEditGroupMessage } from '@/lib/queries/groupChat';
import { logEvent } from '@/lib/analytics';

type Props = {
  message: GroupChatMessage;
  token?: string;
  groupId: string;
  onDelete?: (id: string) => void;
  onEdit?: (message: GroupChatMessage) => void;
};

export default function ChatMessageCard({ message, token, groupId, onDelete, onEdit }: Props) {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const voteMutation = useVoteGroupPoll(token, groupId);
  const closeMutation = useCloseGroupPoll(token, groupId);
  const myUserId = user?.id;

  const openTrail = () => {
    if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    logEvent('chat_trail_opened', { trail_id: message.payload?.trail_id });
    router.push(`/trail/${message.payload?.trail_id}`);
  };

  const openMap = () => {
    if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    logEvent('chat_meetpoint_opened', { lat: message.payload?.lat });
    // opens the device maps app at the meet point (same pattern as trail get_directions)
    router.push(
      `https://www.google.com/maps/search/?api=1&query=${message.payload?.lat},${message.payload?.lng}` as any,
    );
  };

  const vote = (optionIndex: number) => {
    if (!message.poll || message.poll.isClosed) return;
    if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    logEvent('poll_voted', { poll_id: message.poll.id });
    voteMutation.mutate({ pollId: message.poll.id, optionIndex });
  };

  const isMine = message.userId === myUserId;

  const showActions = () => {
    if (!isMine) return;
    const buttons: any[] = [];
    const canEdit =
      message.type === 'text' ||
      message.type === 'location' ||
      (message.type === 'poll' && !!message.poll);
    if (canEdit && onEdit) {
      buttons.push({
        text: t('packChat.editMessage'),
        onPress: () => onEdit(message),
      });
    }
    buttons.push({
      text: message.type === 'poll' ? t('packChat.deletePoll') : t('packChat.deleteMessage'),
      style: 'destructive',
      onPress: () => onDelete?.(message.id),
    });
    buttons.push({ text: t('common.cancel'), style: 'cancel' });
    Alert.alert(message.senderName, message.body ?? undefined, buttons);
  };

  const timeLabel = message.createdAt
    ? new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <View style={[styles.wrap, isMine && styles.wrapMine]}>
      <View style={[styles.senderRow, isMine && styles.senderRowMine]}>
        <Text style={styles.sender}>{message.senderName}</Text>
        <Text style={styles.time}>{timeLabel}{message.editedAt ? ` · ${t('packChat.edited')}` : ''}</Text>
      </View>
      {message.type === 'trail' && (
        <TouchableOpacity style={[styles.card, isMine && styles.cardMine]} onPress={openTrail} onLongPress={showActions} delayLongPress={300} activeOpacity={0.9}>
          <View style={styles.cardHead}>
            <Text style={styles.cardTitle}>{message.payload?.name ?? t('packChat.trail')}</Text>
            <ArrowRight size={16} color={theme.accentPrimary} />
          </View>
          <Text style={styles.cardSub}>{t('packChat.openTrail')}</Text>
        </TouchableOpacity>
      )}
      {message.type === 'location' && (
        <TouchableOpacity style={[styles.card, isMine && styles.cardMine]} onPress={openMap} onLongPress={showActions} delayLongPress={300} activeOpacity={0.9}>
          <View style={styles.cardHead}>
            <MapPin size={16} color={theme.accentSecondary} />
            <Text style={[styles.cardTitle, { marginLeft: 6 }]} numberOfLines={1}>
              {message.payload?.label ?? t('packChat.meetPoint')}
            </Text>
          </View>
          <Text style={styles.cardSub}>
            {`📍 ${message.payload?.lat?.toFixed(5)}, ${message.payload?.lng?.toFixed(5)}`}
          </Text>
        </TouchableOpacity>
      )}
      {message.type === 'text' && (
        <TouchableOpacity style={[styles.bubble, isMine && styles.bubbleMine]} onLongPress={showActions} delayLongPress={300} activeOpacity={0.8}>
          <Text style={[styles.bubbleText, isMine && styles.bubbleTextMine]}>{message.body}</Text>
        </TouchableOpacity>
      )}
      {message.type === 'poll' && message.poll && (
        <TouchableOpacity style={[styles.card, isMine && styles.cardMine]} onLongPress={showActions} delayLongPress={300} activeOpacity={0.9} onPress={() => {}}>
          <View style={styles.cardHead}>
            <Vote size={16} color={theme.accentPrimary} />
            <Text style={[styles.cardTitle, { marginLeft: 6 }]}>{message.poll.question}</Text>
          </View>
          {message.poll.options.map((opt, idx) => {
            const count = message.poll?.tally[String(idx)] ?? 0;
            const mine = (message.poll?.myVotes ?? []).includes(idx);
            const pct = message.poll?.totalVotes ? Math.round((count / message.poll.totalVotes) * 100) : 0;
            return (
              <TouchableOpacity
                key={idx}
                style={[styles.pollRow, mine && styles.pollRowMine]}
                disabled={message.poll?.isClosed}
                onPress={() => vote(idx)}
                activeOpacity={message.poll?.isClosed ? 1 : 0.7}
              >
                <Text style={[styles.pollOption, mine && styles.pollOptionMine]}>{opt}</Text>
                <View style={styles.pollRight}>
                  {mine && <Check size={14} color={theme.accentSecondary} />}
                  <Text style={styles.pollCount}>{`${count} (${pct}%)`}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
          <View style={styles.pollFooter}>
            <Text style={styles.pollMeta}>
              {message.poll!.isClosed ? t('packChat.pollClosed') : t('packChat.tapToVote')}
              {` · ${t('packChat.votes', { count: message.poll!.totalVotes })}`}
            </Text>
          </View>
          {!message.poll!.isClosed && message.poll!.createdBy === myUserId && (
            <TouchableOpacity
              style={styles.closePollBtn}
              onPress={() => {
                logEvent('poll_closed', { poll_id: message.poll?.id });
                closeMutation.mutate({ pollId: message.poll!.id });
              }}
            >
              <Text style={styles.closePollText}>{t('packChat.closePoll')}</Text>
            </TouchableOpacity>
          )}
        </TouchableOpacity>
      )}

    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { marginHorizontal: 12, marginVertical: 6 },
  senderRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 3, marginLeft: 2 },
  sender: { fontSize: 11, color: theme.textMuted },
  time: { fontSize: 10, color: theme.textMuted, opacity: 0.7 },
  bubble: {
    backgroundColor: theme.backgroundSecondary,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 10,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  bubbleText: { color: theme.textPrimary, fontSize: 15 },
  bubbleTextMine: { color: theme.backgroundPrimary, fontWeight: '500' },
  wrapMine: { alignItems: 'flex-end' },
  senderRowMine: { flexDirection: 'row-reverse' },
  bubbleMine: {
    backgroundColor: theme.accentPrimary,
    alignSelf: 'flex-end',
  },
  cardMine: {
    backgroundColor: theme.backgroundSecondaryVarient,
    alignSelf: 'flex-end',
  },
  card: {
    backgroundColor: theme.backgroundSecondary,
    borderRadius: 14,
    padding: 12,
    alignSelf: 'stretch',
  },
  cardHead: { flexDirection: 'row', alignItems: 'center' },
  cardTitle: { fontSize: 15, fontWeight: '600', color: theme.textPrimary },
  cardSub: { fontSize: 12, color: theme.textMuted, marginTop: 4 },
  pollRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 6,
  },
  pollRowMine: { borderColor: theme.accentPrimary },
  pollOption: { color: theme.textPrimary, fontSize: 14, flex: 1 },
  pollOptionMine: { color: theme.accentPrimary, fontWeight: '600' },
  pollRight: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  pollCount: { color: theme.textMuted, fontSize: 12, fontVariant: ['tabular-nums'] },
  pollFooter: { marginTop: 8, flexDirection: 'row', alignItems: 'center', gap: 6 },
  pollMeta: { color: theme.textMuted, fontSize: 11 },
  closePollBtn: { marginTop: 6, alignSelf: 'flex-end' },
  closePollText: { color: theme.textMuted, fontSize: 11, textDecorationLine: 'underline' },
  deleteBtn: { marginTop: 4, alignSelf: 'flex-end' },
  deleteText: { color: theme.textMuted, fontSize: 11, textDecorationLine: 'underline' },
});